import React from 'react';
import './ResultsTable.css';

// Shared by the Results page (/results) and the results box on Page 3.
// Numbers come from the indy-results Lambda (Google Search) or from Admin edits — always unofficial.

export const API_URL = 'https://0uuj9e2rh5.execute-api.us-east-2.amazonaws.com';
export const POLLS_CLOSE = Date.parse('2026-11-04T00:00:00Z'); // 7 pm ET on Nov 3 — first polls close
export const pollsClosed = () => Date.now() >= POLLS_CLOSE;

export const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky',
  LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};
export const OFFICE_NAMES = {
  senate: 'U.S. Senate', house: 'U.S. House', governor: 'Governor', 'lieutenant-governor': 'Lieutenant Governor',
  'attorney-general': 'Attorney General', 'secretary-of-state': 'Secretary of State', treasurer: 'State Treasurer',
  auditor: 'State Auditor', 'superintendent-of-schools': 'Superintendent of Schools', 'agriculture-commissioner': 'Agriculture Commissioner',
  'insurance-commissioner': 'Insurance Commissioner', 'labor-commissioner': 'Labor Commissioner', 'land-commissioner': 'Land Commissioner',
  'tax-commissioner': 'Tax Commissioner', 'public-service-commission': 'Public Service Commission', 'railroad-commission': 'Railroad Commission',
  'mine-inspector': 'State Mine Inspector', 'board-of-equalization': 'Board of Equalization', 'governors-council': "Governor's Council",
  'executive-council': 'Executive Council', 'oha-trustee': 'Office of Hawaiian Affairs', 'board-of-education': 'State Board of Education',
  'university-regents': 'University Regents', 'state-senate': 'State Senate', 'state-house': 'State House', measures: 'Ballot Measures',
};
export const AUDITOR_TITLES = {
  AL: 'State Auditor', AR: 'State Auditor', CA: 'State Controller', CT: 'State Comptroller', DE: 'State Auditor', IA: 'Auditor of State',
  ID: 'State Controller', IL: 'State Comptroller', IN: 'State Comptroller', MA: 'Auditor of the Commonwealth', MD: 'State Comptroller',
  MN: 'State Auditor', MO: 'State Auditor', NE: 'Auditor of Public Accounts', NM: 'State Auditor', NV: 'State Controller',
  NY: 'State Comptroller', OH: 'Auditor of State', OK: 'State Auditor and Inspector', SC: 'Comptroller General', SD: 'State Auditor',
  VT: 'Auditor of Accounts', WY: 'State Auditor',
};

// "Illinois Governor", "Illinois U.S. House District 9", "Illinois State Senate District 12", …
export function raceTitleFromRow(r) {
  const st = STATE_NAMES[r.state] || r.state || '';
  if (r.racetype === 'measures') return `${st} Ballot Measures`;
  if (r.racetype === 'house') {
    const d = String(r.raceId || '').split('-').pop();
    return `${st} U.S. House ${/^(AL|0+)$/i.test(d) ? 'At-Large' : `District ${Number(d) || d}`}`;
  }
  if (r.racetype === 'auditor') return `${st} ${AUDITOR_TITLES[r.state] || 'State Auditor'}`;
  if (r.raceLabel) return r.raceLabel.includes(st) ? r.raceLabel : `${st} ${r.raceLabel}`;
  return `${st} ${OFFICE_NAMES[r.racetype] || r.racetype || ''}`.trim();
}

export function partyColor(party) {
  const p = (party || '').toLowerCase();
  if (p.includes('democrat')) return '#00AEF3';
  if (p.includes('republican')) return '#B22234';
  if (p.includes('libertarian')) return '#F38C00';
  if (p.includes('green')) return '#00A86B';
  if (p.includes('independent')) return '#6B7280';
  return '#808080';
}

const RANK = ['', 'not_reported', 'counting', 'runoff', 'uncontested', 'called', 'final'];
const pick = (rows, k) => (rows.find(r => r[k]) || {})[k] || '';
const latest = rows => rows.map(r => r.resultUpdated || '').sort().pop() || '';

// Candidate rows (from /candidates) → the same shape the Lambda stores in the results index
export function summaryFromRows(rows, racetype) {
  if (!rows || !rows.length) return null;
  const first = rows[0];
  if ((racetype || first.racetype) === 'measures') {
    const st = rows.map(m => m.resultStatus || '');
    const status = st.length && st.every(x => x === 'passed' || x === 'failed') ? 'called'
      : st.some(x => ['passed', 'failed', 'counting'].includes(x)) ? 'counting' : '';
    return {
      id: first.raceId, t: 'measures', title: raceTitleFromRow(first), status, rep: pick(rows, 'resultReporting'),
      src: pick(rows, 'resultSource'), asOf: pick(rows, 'resultAsOf'), upd: latest(rows),
      m: [...rows].sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)).map(m => ({
        n: m.shortName || m.officialTitle || '', s: m.resultStatus || '', yv: m.resultYesVotes || '', nv: m.resultNoVotes || '',
        y: m.resultYesPct || '', no: m.resultNoPct || '',
      })),
    };
  }
  const status = rows.map(r => r.resultStatus || '').reduce((a, b) => (RANK.indexOf(b) > RANK.indexOf(a) ? b : a), '');
  return {
    id: first.raceId, t: first.racetype || racetype, title: raceTitleFromRow(first), status,
    rep: pick(rows, 'resultReporting'), by: pick(rows, 'resultCalledBy'), src: pick(rows, 'resultSource'), asOf: pick(rows, 'resultAsOf'), upd: latest(rows),
    c: rows.map(c => ({ n: c.name, p: c.party || '', v: c.resultVotes || '', pc: c.resultPct || '', w: c.resultWinner === 'true' ? 1 : 0 }))
      .sort((a, b) => (Number(b.v) || 0) - (Number(a.v) || 0) || (Number(b.pc) || 0) - (Number(a.pc) || 0) || a.n.localeCompare(b.n)),
  };
}

export const hasNumbers = race => !!race && (
  (race.c || []).some(c => c.v || c.pc || c.w) || (race.m || []).some(m => m.y || m.no || m.s === 'passed' || m.s === 'failed'));

export function statusText(race) {
  const s = race?.status || '';
  if (s === 'uncontested') return 'Unopposed';
  if (s === 'final') return 'Final results';
  if (s === 'called') return race.t === 'measures' ? 'All measures decided' : `Projected winner${race.by ? ` · called by ${race.by}` : ''}`;
  if (s === 'runoff') return 'Headed to a runoff';
  if (s === 'counting') return `Votes being counted${race.rep ? ` · ${race.rep}% counted` : ''}`;
  return pollsClosed() ? 'No results reported yet' : 'Polls close November 3';
}
const statusClass = s => (s === 'final' || s === 'called' || s === 'uncontested') ? 'done' : (s === 'counting' || s === 'runoff') ? 'live' : 'wait';

const fmtVotes = v => { const n = Number(v); return v !== '' && Number.isFinite(n) ? n.toLocaleString('en-US') : '—'; };
function pctOf(race) {
  // Use the published % when we have it, otherwise work it out from the votes
  const total = (race.c || []).reduce((s, c) => s + (Number(c.v) || 0), 0);
  return c => (c.pc !== '' && c.pc !== undefined ? Number(c.pc) : (total && c.v ? (Number(c.v) / total) * 100 : null));
}
function host(url) { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; } }
export function fmtUpdated(iso) {
  const t = Date.parse(iso || '');
  if (!t) return '';
  return `${new Date(t).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ET`;
}

export function StatusBadge({ race }) {
  return <span className={`rt-badge rt-badge--${statusClass(race?.status)}`}>{statusText(race)}</span>;
}

function MeasureRows({ race }) {
  return (
    <div className="rt-measures">
      {race.m.map((m, i) => {
        const y = m.y !== '' ? Number(m.y) : null, no = m.no !== '' ? Number(m.no) : null;
        return (
          <div key={i} className="rt-measure">
            <div className="rt-measure-head">
              <span className="rt-measure-name">{m.n}</span>
              {m.s === 'passed' && <span className="rt-outcome rt-outcome--yes">✓ Passed</span>}
              {m.s === 'failed' && <span className="rt-outcome rt-outcome--no">✗ Failed</span>}
              {m.s === 'counting' && <span className="rt-outcome">Counting</span>}
            </div>
            {y !== null ? (
              <>
                <div className="rt-yesno-bar" aria-hidden="true">
                  <span className="rt-yes" style={{ width: `${Math.max(0, Math.min(100, y))}%` }} />
                  <span className="rt-no" style={{ width: `${Math.max(0, Math.min(100, no ?? 0))}%` }} />
                </div>
                <div className="rt-yesno-nums">
                  <span>Yes {y.toFixed(1)}%{m.yv ? ` · ${fmtVotes(m.yv)}` : ''}</span>
                  <span>No {no !== null ? `${no.toFixed(1)}%` : '—'}{m.nv ? ` · ${fmtVotes(m.nv)}` : ''}</span>
                </div>
              </>
            ) : <div className="rt-none">No results yet</div>}
          </div>
        );
      })}
    </div>
  );
}

// One race: status, candidates with votes / % / bar, winner check, source and time
export function ResultsTable({ race, showTitle = false, titleHref = '', headingLevel = 3 }) {
  if (!race) return null;
  const Heading = `h${headingLevel}`;
  const pct = pctOf(race);
  const showNums = hasNumbers(race);
  return (
    <div className="rt">
      {showTitle && (
        <div className="rt-top">
          <Heading className="rt-title">{titleHref ? <a href={titleHref} data-nav="1">{race.title}</a> : race.title}</Heading>
          <StatusBadge race={race} />
        </div>
      )}
      {!showTitle && <div className="rt-top rt-top--solo"><StatusBadge race={race} /></div>}
      {race.t === 'measures' ? <MeasureRows race={race} /> : (
        <table className="rt-table">
          <thead>
            <tr><th scope="col">Candidate</th><th scope="col" className="rt-num">Votes</th><th scope="col" className="rt-num">%</th></tr>
          </thead>
          <tbody>
            {(race.c || []).map((c, i) => {
              const p = pct(c);
              return (
                <tr key={i} className={c.w ? 'rt-winner' : ''}>
                  <td>
                    <div className="rt-cand">
                      <span className="rt-dot" style={{ background: partyColor(c.p) }} />
                      <span className="rt-name">{c.n}{c.w ? <span className="rt-check" title="Winner"> ✓</span> : null}</span>
                      {c.p && <span className="rt-party">{c.p}</span>}
                    </div>
                    {showNums && p !== null && (
                      <div className="rt-bar" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, p))}%`, background: partyColor(c.p) }} /></div>
                    )}
                  </td>
                  <td className="rt-num">{fmtVotes(c.v)}</td>
                  <td className="rt-num">{p !== null ? `${p.toFixed(1)}%` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p className="rt-meta">
        Unofficial results
        {race.src && host(race.src) && <> · Source: <a href={race.src} target="_blank" rel="noopener noreferrer nofollow">{host(race.src)} ↗</a></>}
        {race.asOf ? ` · As of ${race.asOf}` : race.upd ? ` · Checked ${fmtUpdated(race.upd)}` : ''}
      </p>
    </div>
  );
}

export default ResultsTable;
